import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { CatalogObjectTypes, EmailNotifications, Preferences } from '@mirage-projects/cw-models';

import { MagnetService } from '../../abstract-classes';
import { EntryPoint } from '../entry-point/entry-point.enum';
import { EntryPointService } from '../entry-point/entry-point.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

export type NotificationObjectType = 'all' | CatalogObjectTypes;

export type NotificationEventType =
    | 'CATALOG_OBJECT_UPDATE'
    | 'CATALOG_VISIBILITY_GRANTED'
    | 'CATALOG_VISIBILITY_REVOKED'
    | 'JOB_STARTED'
    | 'JOB_FINISHED';

@Injectable({
    providedIn: 'root',
})
export class NotificationsSettingsService {
    readonly LOCAL_STORAGE_NOTIFICATION_SETTINGS = 'notificationSettings';

    constructor(
        private httpClient: HttpClient,
        private entryPointService: EntryPointService,
        private localStorageService: LocalStorageService,
        private magnetService: MagnetService
    ) {
        const localSettings = this.getAllNotificationPreferences();
        if (!localSettings) {
            const enableIfSite = this.magnetService.isSite();
            this.initDefaultSettings(enableIfSite);
        }
    }

    /**
     * Sets the notification settings object.
     */
    setAllNotificationPreferences(settings: any): void {
        this.localStorageService.setItem(this.LOCAL_STORAGE_NOTIFICATION_SETTINGS, settings, true);
    }

    /**
     * Gets the notification settings object from local storage
     */
    getAllNotificationPreferences(): any {
        const notificationSettingsStr = this.localStorageService.getItem(this.LOCAL_STORAGE_NOTIFICATION_SETTINGS);
        return notificationSettingsStr ? JSON.parse(notificationSettingsStr) : null;
    }

    /**
     * Gets the value for an eventType-objectType pair notification.
     */
    getNotificationPreference(eventType: NotificationEventType, objectType?: NotificationObjectType): boolean {
        const notificationSettings = this.getAllNotificationPreferences();
        if (notificationSettings) {
            if (!objectType) {
                return notificationSettings[eventType]; // Nowadays this is used by the quota event because of the Notification API
            }
            return notificationSettings[objectType][eventType];
        }
        return true;
    }

    setEmailPreferences(emailSettings: EmailNotifications): Observable<Preferences> {
        const emailNotifications = {
            emailNotifications: emailSettings,
        };
        const entryPointPreferences = this.entryPointService.getResource(EntryPoint.PREFERENCES);
        return this.httpClient.put<Preferences>(`${entryPointPreferences}`, emailNotifications);
    }

    getEmailPreferences(): Observable<Preferences> {
        const entryPointPreferences = this.entryPointService.getResource(EntryPoint.PREFERENCES);
        return this.httpClient.get<Preferences>(`${entryPointPreferences}`);
    }

    /**
     * Gets the preferences object for an object's type (for example, 'layers').
     * @return {object} If it exists, the notification settings for the object's type indicated
     */
    getAllNotificationPreferencesFromObject(objectType: NotificationObjectType): any {
        const notificationSettings = this.getAllNotificationPreferences();
        return notificationSettings ? notificationSettings[objectType] : undefined; // Be carefull when checking the value received
    }

    /**
     * Sets true or false for an object-event pair notification.
     * Be careful! It is not adapted for QUOTA because this function is not used.
     */
    setNotificationPreference(objectType: NotificationObjectType, eventType: NotificationEventType, value: boolean): void {
        const notificationSettings = this.getAllNotificationPreferences();
        notificationSettings[objectType][eventType] = value;
        this.setAllNotificationPreferences(notificationSettings);
    }

    /** Set up local notification settings to default values */
    initDefaultSettings(defaultValue = false): void {
        const localSettings = {
            all: defaultValue,
            projects: {
                CATALOG_OBJECT_UPDATE: defaultValue,
                CATALOG_VISIBILITY_GRANTED: defaultValue,
                CATALOG_VISIBILITY_REVOKED: defaultValue,
                JOB_FINISHED: defaultValue,
                JOB_STARTED: defaultValue,
            },
            layers: {
                CATALOG_OBJECT_UPDATE: defaultValue,
                CATALOG_VISIBILITY_GRANTED: defaultValue,
                CATALOG_VISIBILITY_REVOKED: defaultValue,
                JOB_FINISHED: defaultValue,
                JOB_STARTED: defaultValue,
            },
            sites: {
                CATALOG_OBJECT_UPDATE: defaultValue,
                CATALOG_VISIBILITY_GRANTED: defaultValue,
                CATALOG_VISIBILITY_REVOKED: defaultValue,
                JOB_FINISHED: defaultValue,
                JOB_STARTED: defaultValue,
            },
            categories: {
                CATALOG_OBJECT_UPDATE: defaultValue,
                CATALOG_VISIBILITY_GRANTED: defaultValue,
                CATALOG_VISIBILITY_REVOKED: defaultValue,
            },
            QUOTA_EXCEEDED: defaultValue,
        };

        this.setAllNotificationPreferences(localSettings);
    }
}
